import * as React from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./diagram-palette.scss";

/**
 * Kuika's own BPMN palette — a compact category rail (Tasks / Gateways / Events / Tools / Phase);
 * hovering a category opens a flyout listing its tools with labels. Replaces the stock
 * `.djs-palette` (hidden via CSS), whose flat entry list had outgrown DOM-hiding hacks — this is
 * plain HTML driving the same diagram-js services the stock palette uses (`create.start(event,
 * shape)` begins the normal drag-to-place interaction from any DOM element; tools call their
 * activate APIs exactly as bpmn's PaletteProvider does). Creation attrs mirror the retired
 * customPalette entries, including the behavioural flags (`isAdaptiveTimerEvent`,
 * `needsInternalElements`, subprocess `autoSelect` hint, `isHappyPathPhase`).
 */

interface IModelerLike {
  get(name: string): unknown;
}

interface IBusinessObjectLike {
  name?: string;
  eventDefinitions?: IBusinessObjectLike[];
  isHappyPathPhase?: boolean;
}

interface IShapeElementLike {
  needsInternalElements?: boolean;
  isAdaptiveTimerEvent?: boolean;
}

interface IShapeAttrs {
  type: string;
  businessObject: IBusinessObjectLike;
  width?: number;
  height?: number;
  isExpanded?: boolean;
}

interface ICreateHints {
  hints?: { autoSelect?: IShapeElementLike[] };
}

interface ICreateLike {
  start(event: Event, shape: IShapeElementLike, context?: ICreateHints): void;
}

interface IElementFactoryLike {
  createShape(attrs: IShapeAttrs): IShapeElementLike;
}

interface IBpmnFactoryLike {
  create(type: string): IBusinessObjectLike;
}

interface IHandToolLike {
  activateHand(event: Event): void;
}

interface ISelectionToolLike {
  activateSelection(event: Event): void;
}

interface IGlobalConnectLike {
  start(event: Event): void;
}

interface IPaletteItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** True for the canvas tools (activate on click); creates run on mousedown to begin the drag. */
  isTool?: boolean;
  run(event: Event): void;
}

interface IPaletteCategory {
  key: string;
  label: string;
  icon: React.ReactNode;
  items: IPaletteItem[];
}

interface IDiagramPaletteProps {
  modeler: IModelerLike | undefined;
}

function bpmnIcon(glyph: string): React.ReactNode {
  return <span className={`kuika-diagram-palette__glyph bpmn-icon-${glyph}`} aria-hidden="true" />;
}

const DiagramPalette: React.FC<IDiagramPaletteProps> = ({ modeler }: IDiagramPaletteProps) => {
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);

  if (!modeler) return null;

  const startCreate = (event: Event, buildShape: () => IShapeElementLike, context?: ICreateHints): void => {
    try {
      const create: ICreateLike = modeler.get("create") as ICreateLike;
      create.start(event, buildShape(), context);
    } catch {
      // palette action must never crash the editor.
    }
  };

  const factories = (): { elementFactory: IElementFactoryLike; bpmnFactory: IBpmnFactoryLike } => ({
    elementFactory: modeler.get("elementFactory") as IElementFactoryLike,
    bpmnFactory: modeler.get("bpmnFactory") as IBpmnFactoryLike
  });

  const createSimple =
    (type: string): ((event: Event) => void) =>
    (event: Event): void => {
      startCreate(event, (): IShapeElementLike => {
        const { elementFactory, bpmnFactory } = factories();
        return elementFactory.createShape({ type, businessObject: bpmnFactory.create(type) });
      });
    };

  const createTimer = (event: Event): void => {
    startCreate(event, (): IShapeElementLike => {
      const { elementFactory, bpmnFactory } = factories();
      const businessObject: IBusinessObjectLike = bpmnFactory.create("bpmn:IntermediateCatchEvent");
      businessObject.eventDefinitions = [bpmnFactory.create("bpmn:TimerEventDefinition")];
      const shape: IShapeElementLike = elementFactory.createShape({
        type: "bpmn:IntermediateCatchEvent",
        businessObject
      });
      shape.isAdaptiveTimerEvent = true; // may attach as a boundary timer (customBpmnRules)
      return shape;
    });
  };

  const createSubprocess = (event: Event): void => {
    try {
      const { elementFactory, bpmnFactory } = factories();
      const businessObject: IBusinessObjectLike = bpmnFactory.create("bpmn:SubProcess");
      businessObject.name = "Sub Process";
      const shape: IShapeElementLike = elementFactory.createShape({
        type: "bpmn:SubProcess",
        businessObject,
        isExpanded: false
      });
      shape.needsInternalElements = true; // internal start/end added after placement
      const create: ICreateLike = modeler.get("create") as ICreateLike;
      create.start(event, shape, { hints: { autoSelect: [shape] } });
    } catch {
      // palette action must never crash the editor.
    }
  };

  const createLinkedProcess = (event: Event): void => {
    startCreate(event, (): IShapeElementLike => {
      const { elementFactory, bpmnFactory } = factories();
      const businessObject: IBusinessObjectLike = bpmnFactory.create("bpmn:CallActivity");
      businessObject.name = "Linked Process";
      return elementFactory.createShape({ type: "bpmn:CallActivity", businessObject });
    });
  };

  const createPhase = (event: Event): void => {
    startCreate(event, (): IShapeElementLike => {
      const { elementFactory, bpmnFactory } = factories();
      const businessObject: IBusinessObjectLike = bpmnFactory.create("bpmn:Group");
      businessObject.isHappyPathPhase = true;
      return elementFactory.createShape({ type: "bpmn:Group", businessObject, width: 260, height: 300 });
    });
  };

  const categories: IPaletteCategory[] = [
    {
      key: "tasks",
      label: "Activities",
      icon: bpmnIcon("task"),
      items: [
        { key: "user-task", label: "User Task", icon: bpmnIcon("user-task"), run: createSimple("bpmn:UserTask") },
        { key: "system-task", label: "System Task", icon: bpmnIcon("task"), run: createSimple("bpmn:Task") },
        { key: "subprocess", label: "Subprocess", icon: bpmnIcon("subprocess-collapsed"), run: createSubprocess },
        { key: "linked-process", label: "Linked Process", icon: bpmnIcon("call-activity"), run: createLinkedProcess }
      ]
    },
    {
      key: "gateways",
      label: "Gateways",
      icon: bpmnIcon("gateway-none"),
      items: [
        {
          key: "gw-exclusive",
          label: "Exclusive Gateway",
          icon: bpmnIcon("gateway-xor"),
          run: createSimple("bpmn:ExclusiveGateway")
        },
        {
          key: "gw-parallel",
          label: "Parallel Gateway",
          icon: bpmnIcon("gateway-parallel"),
          run: createSimple("bpmn:ParallelGateway")
        },
        {
          key: "gw-inclusive",
          label: "Inclusive Gateway",
          icon: bpmnIcon("gateway-or"),
          run: createSimple("bpmn:InclusiveGateway")
        }
      ]
    },
    {
      key: "events",
      label: "Events",
      icon: bpmnIcon("start-event-none"),
      items: [
        {
          key: "start",
          label: "Start Event",
          icon: bpmnIcon("start-event-none"),
          run: createSimple("bpmn:StartEvent")
        },
        { key: "end", label: "End Event", icon: bpmnIcon("end-event-none"), run: createSimple("bpmn:EndEvent") },
        { key: "timer", label: "Timer Event", icon: bpmnIcon("intermediate-event-catch-timer"), run: createTimer }
      ]
    },
    {
      key: "tools",
      label: "Tools",
      icon: bpmnIcon("hand-tool"),
      items: [
        {
          key: "hand",
          label: "Hand Tool (pan)",
          icon: bpmnIcon("hand-tool"),
          isTool: true,
          run: (event: Event): void => (modeler.get("handTool") as IHandToolLike).activateHand(event)
        },
        {
          key: "lasso",
          label: "Lasso Select",
          icon: bpmnIcon("lasso-tool"),
          isTool: true,
          run: (event: Event): void => (modeler.get("lassoTool") as ISelectionToolLike).activateSelection(event)
        },
        {
          key: "space",
          label: "Space Tool",
          icon: bpmnIcon("space-tool"),
          isTool: true,
          run: (event: Event): void => (modeler.get("spaceTool") as ISelectionToolLike).activateSelection(event)
        },
        {
          key: "connect",
          label: "Connect Tool",
          icon: bpmnIcon("connection-multi"),
          isTool: true,
          run: (event: Event): void => (modeler.get("globalConnect") as IGlobalConnectLike).start(event)
        }
      ]
    },
    {
      key: "phase",
      label: "Happy Path",
      icon: <KuikaSvg name="layout-columns" width={17} height={17} color="currentColor" />,
      items: [
        {
          key: "happy-path-phase",
          label: "Happy Path Phase",
          icon: <KuikaSvg name="layout-columns" width={16} height={16} color="currentColor" />,
          run: createPhase
        }
      ]
    }
  ];

  const runItem = (item: IPaletteItem, e: React.MouseEvent | React.DragEvent): void => {
    setActiveCategory(null);
    try {
      item.run(e.nativeEvent);
    } catch {
      // palette action must never crash the editor.
    }
  };

  return (
    <div className="kuika-diagram-palette" data-testid="diagramPalette" onMouseLeave={() => setActiveCategory(null)}>
      {categories.map((category: IPaletteCategory) => (
        <div
          key={category.key}
          className={`kuika-diagram-palette__cat${activeCategory === category.key ? " is-active" : ""}`}
          onMouseEnter={() => setActiveCategory(category.key)}
        >
          {/* KOA-15840 replaced bpmn-js's flat `.djs-palette` with these categories, so the stock
              `[data-action="create.task.user"]` hooks are gone. The category is icon-only and an
              item's label is a span beside its glyph, so neither is addressable as text. */}
          <button
            type="button"
            className="kuika-diagram-palette__cat-btn"
            aria-label={category.label}
            data-testid={`diagramPaletteCategory-${category.key}`}
          >
            {category.icon}
          </button>
          {activeCategory === category.key && (
            <div className="kuika-diagram-palette__flyout" role="menu" aria-label={category.label}>
              <div className="kuika-diagram-palette__flyout-title">{category.label}</div>
              {category.items.map((item: IPaletteItem) => (
                <button
                  type="button"
                  key={item.key}
                  className="kuika-diagram-palette__item"
                  role="menuitem"
                  data-testid={`diagramPaletteItem-${item.key}`}
                  // STOCK palette semantics: CLICK starts the create — the shape follows the
                  // cursor and is placed with a click on the canvas (never dropped immediately);
                  // a real press-and-drag goes through dragstart (diagram-js preventDefaults the
                  // native DnD and tracks the pointer itself). Tools simply activate on click.
                  draggable={!item.isTool}
                  onClick={(e: React.MouseEvent) => runItem(item, e)}
                  onDragStart={item.isTool ? undefined : (e: React.DragEvent) => runItem(item, e)}
                >
                  <span className="kuika-diagram-palette__item-icon">{item.icon}</span>
                  <span className="kuika-diagram-palette__item-label">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DiagramPalette;
